// EWM-866 (ADR-003) — async enqueue + poll for AI Advisory. Decoupled from the request/response
// lifecycle: `POST .../messages/async` returns immediately, and the client polls
// `GET .../responses/{responseId}` until it resolves. Final-only, not progressive — no
// `textSoFar`/cursor, since `background: true` partial-output behaviour is undocumented (see the
// ADR). The status-interpretation logic is extracted as a pure function so it is unit-testable
// without standing up the React hook or fake fetch/timers, matching ./reconcile.ts's convention.

export interface SendMessageAsyncResponse {
  userMessageId: string;
  responseId: string;
}

export interface GetAiAdvisoryResponseResponse {
  /** "Queued" | "InProgress" | "Completed" | "Failed" */
  status: string;
  assistantMessageId?: string;
  content?: string;
  responseAt?: string;
  tokenCount?: number;
  processingTime?: number;
}

export type PollOutcome =
  | { kind: 'done'; assistantMessageId: string; content: string; processingTime: number }
  | { kind: 'failed'; message: string }
  | { kind: 'continue' };

/**
 * Decides what a poll response means for the UI: render the final answer, surface a failure, or
 * keep polling. `Completed` without both an id and content is treated as `continue` rather than
 * `done` — a defensive guard against a malformed/partial payload, not a status the server is
 * documented to send.
 */
export function interpretPollResponse(response: GetAiAdvisoryResponseResponse): PollOutcome {
  if (response.status === 'Completed' && response.assistantMessageId && response.content) {
    return {
      kind: 'done',
      assistantMessageId: response.assistantMessageId,
      content: response.content,
      processingTime: response.processingTime ?? 0,
    };
  }

  if (response.status === 'Failed') {
    return {
      kind: 'failed',
      message: 'The AI Advisory response could not be generated. Please try again.',
    };
  }

  return { kind: 'continue' };
}

/**
 * EY pentest 26-B3FC-65B76: the API refuses a message that re-instructs the assistant or probes its
 * configuration with 400 + `AI_ADVISORY_REQUEST_OUT_OF_SCOPE`. Its `detail` tells the user what to
 * do instead, so show it; every other failure keeps the status-code message (statusText is empty
 * over HTTP/2).
 */
export function describeStartFailure(
  status: number,
  statusText: string,
  problem: { errorCode?: string; detail?: string } | null,
): string {
  if (problem?.errorCode === 'AI_ADVISORY_REQUEST_OUT_OF_SCOPE' && problem.detail) {
    return problem.detail;
  }

  return `Failed to start AI Advisory generation (HTTP ${status}${statusText ? ` ${statusText}` : ''})`;
}
